import { useRef, useState } from "react";
import { useConfirm } from "../../../components/Dialog";
import { IconDownload } from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";

type Props = {
  leadId: number;
  leadUlid: string;
  onAfterChange: () => void;
};

type ActionState = null | "downloading" | "regenerating" | "substituting" | "replacing";

const BASE_URL =
  process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") ??
  "http://localhost:8000";

export default function ContractActions({
  leadId,
  leadUlid,
  onAfterChange,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const substituteInput = useRef<HTMLInputElement | null>(null);
  const replaceInput = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<ActionState>(null);
  const [feedback, setFeedback] = useState<
    | { kind: "success" | "error"; message: string }
    | null
  >(null);

  const reportSuccess = (msg: string) => setFeedback({ kind: "success", message: msg });
  const reportError = (msg: string) => setFeedback({ kind: "error", message: msg });

  const headers = (extra: Record<string, string> = {}): Record<string, string> => ({
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  });

  const handleDownload = async () => {
    if (busy) return;
    setBusy("downloading");
    setFeedback(null);
    try {
      const response = await fetch(
        `${BASE_URL}/api/admin/operations/leads/${leadUlid}/contract/download`,
        { headers: headers() },
      );
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(`HTTP ${response.status} ${detail.slice(0, 120)}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `Contract-${leadId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      reportSuccess("Contrato descargado");
    } catch (err) {
      reportError(err instanceof Error ? err.message : "Descarga falló");
    } finally {
      setBusy(null);
    }
  };

  const handleRegenerate = async () => {
    if (busy) return;
    const ok = await confirm({
      title: "Regenerar contrato",
      message:
        "Se generará un contrato nuevo con los datos actuales y se enviará al cliente. ¿Continuar?",
      confirmLabel: "Regenerar y enviar",
      tone: "warning",
    });
    if (!ok) return;
    setBusy("regenerating");
    setFeedback(null);
    try {
      const response = await fetch(
        `${BASE_URL}/api/admin/operations/leads/${leadUlid}/contract/regenerate`,
        { method: "POST", headers: headers() },
      );
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(`HTTP ${response.status} ${detail.slice(0, 120)}`);
      }
      reportSuccess("Contrato regenerado y enviado al cliente");
      onAfterChange();
    } catch (err) {
      reportError(err instanceof Error ? err.message : "Regenerate falló");
    } finally {
      setBusy(null);
    }
  };

  const uploadFile = async (
    file: File,
    endpoint: "substitute" | "replace-signed",
    state: ActionState,
    successMessage: string,
  ) => {
    if (busy) return;
    setBusy(state);
    setFeedback(null);
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await fetch(
        `${BASE_URL}/api/admin/operations/leads/${leadUlid}/contract/${endpoint}`,
        { method: "POST", headers: headers(), body },
      );
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(`HTTP ${response.status} ${detail.slice(0, 120)}`);
      }
      reportSuccess(successMessage);
      onAfterChange();
    } catch (err) {
      reportError(err instanceof Error ? err.message : "Upload falló");
    } finally {
      setBusy(null);
    }
  };

  const handleSubstituteFile = async (file: File | undefined) => {
    if (!file) return;
    const ok = await confirm({
      title: "Sustituir contrato",
      message:
        "Esto cancela los contratos en proceso de este lead y arranca un nuevo flujo de firma con el archivo subido. ¿Continuar?",
      confirmLabel: "Sustituir y enviar a firma",
      tone: "danger",
    });
    if (!ok) return;
    await uploadFile(
      file,
      "substitute",
      "substituting",
      "Contrato sustituido y enviado a firmar",
    );
  };

  const handleReplaceFile = async (file: File | undefined) => {
    if (!file) return;
    const ok = await confirm({
      title: "Reemplazar contrato firmado",
      message:
        "Esto reemplaza el contrato FIRMADO de este lead por el archivo subido. Es una operación destructiva y queda registrada en auditoría. ¿Continuar?",
      confirmLabel: "Reemplazar firmado",
      tone: "danger",
    });
    if (!ok) return;
    await uploadFile(
      file,
      "replace-signed",
      "replacing",
      "Contrato firmado reemplazado",
    );
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: "white",
        padding: "0.75rem 0.9rem",
        borderRadius: "0.55rem",
        borderLeft: "3px solid #0069d9",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        marginBottom: "1rem",
      }}
    >
      <h4
        style={{
          margin: "0 0 0.55rem",
          fontSize: ".7rem",
          textTransform: "uppercase",
          fontWeight: 600,
          color: "#0069d9",
          letterSpacing: "0.5px",
        }}
      >
        Acciones de contrato
      </h4>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
        <ActionButton
          onClick={handleDownload}
          disabled={!!busy}
          loading={busy === "downloading"}
          title="Genera una copia con los datos actuales sin modificar el contrato firmado/enviado"
          tone="neutral"
        >
          <IconDownload style={{ width: ".95rem", height: ".95rem" }} />
          <span>Descargar</span>
        </ActionButton>

        <ActionButton
          onClick={handleRegenerate}
          disabled={!!busy}
          loading={busy === "regenerating"}
          title="Genera un contrato nuevo con los datos actuales y lo envía al cliente"
          tone="primary"
        >
          Regenerar
        </ActionButton>

        <ActionButton
          onClick={() => substituteInput.current?.click()}
          disabled={!!busy}
          loading={busy === "substituting"}
          title="Subir un contrato personalizado y enviarlo al cliente para firma"
          tone="warning"
        >
          Sustituir
        </ActionButton>
        <input
          ref={substituteInput}
          type="file"
          hidden
          accept="application/pdf"
          onChange={(e) => {
            handleSubstituteFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />

        <ActionButton
          onClick={() => replaceInput.current?.click()}
          disabled={!!busy}
          loading={busy === "replacing"}
          title="Reemplazar un contrato ya firmado por uno nuevo"
          tone="danger"
        >
          Reemplazar firmado
        </ActionButton>
        <input
          ref={replaceInput}
          type="file"
          hidden
          accept="application/pdf"
          onChange={(e) => {
            handleReplaceFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>

      {feedback && (
        <div
          style={{
            marginTop: "0.55rem",
            padding: "0.45rem 0.65rem",
            borderRadius: "0.4rem",
            fontSize: ".78rem",
            background: feedback.kind === "success" ? "#d4edda" : "#fde2e2",
            color: feedback.kind === "success" ? "#155724" : "#b32424",
          }}
        >
          {feedback.message}
        </div>
      )}
    </div>
  );
}

function ActionButton({
  onClick,
  disabled,
  loading,
  title,
  tone,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  title: string;
  tone: "primary" | "neutral" | "warning" | "danger";
  children: React.ReactNode;
}) {
  const palette = TONE_PALETTE[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.4rem",
        padding: "0.45rem 0.85rem",
        borderRadius: "0.45rem",
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        color: palette.fg,
        fontSize: ".8rem",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !loading ? 0.6 : 1,
      }}
    >
      {loading ? "…" : children}
    </button>
  );
}

const TONE_PALETTE = {
  primary: { bg: "#0069d9", fg: "white", border: "#0069d9" },
  neutral: { bg: "white", fg: "#1f2933", border: "#dee2e6" },
  warning: { bg: "#fff8e1", fg: "#856404", border: "#ffe69c" },
  danger: { bg: "#fde2e2", fg: "#b32424", border: "#f5c2c2" },
} as const;
